import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { authAPI } from '../../../utils/api';
import AccountSettingsModal from '../../../components/AccountSettingsModal';

const DoctorProfile = () => {
  const navigate = useNavigate();
  const { user, logout, refreshProfile } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [isEditing, setIsEditing] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    bio: '',
    specialization: '',
    license_number: '',
    signature: ''
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        bio: user.bio || '',
        specialization: user.specialization || '',
        license_number: user.license_number || '',
        signature: user.signature || ''
      });
    }
  }, [user]);

  // Mandatory Image Check
  const [showMandatoryModal, setShowMandatoryModal] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('error') === 'missing_image' && !user?.profile_image) {
      setShowMandatoryModal(true);
    } else {
      setShowMandatoryModal(false);
    }
  }, [user, window.location.search]);

  const handleInputChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const updateProfile = async () => {
    try {
      const res = await authAPI.updateProfile(profileData);
      if (res.data?.success) {
        showSuccess('Profile updated successfully');
        setIsEditing(false);
        window.location.reload(); // Refresh to get updated user data
      } else {
        showError(res.data?.message || 'Failed to update profile');
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to update profile');
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('profileImage', file);

    try {
      const res = await authAPI.uploadProfileImage(formData);
      if (res.data?.success) {
        showSuccess('Profile image updated');
        // Update local state first for immediate feedback
        setProfileData({ ...profileData, profile_image: res.data.data.profile_image });

        // Sync the new profile_image to AuthContext and localStorage immediately
        // so ProtectedRoute uses the updated user data without waiting for refreshProfile
        if (user) {
          const updatedUser = { ...user, profile_image: res.data.data.profile_image };
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
        
        // If they are missing a subscription and just uploaded the mandatory image,
        // redirect them to the subscription screen immediately to avoid lag
        if (!user?.is_subscribed && user?.subscription_status !== 'active') {
            navigate('/subscription?role=doctor');
            refreshProfile(); // do it in background
            return;
        }

        // Refresh global user context
        await refreshProfile();
        
        // Remove error param from URL to dismiss modal
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.get('error') === 'missing_image') {
          searchParams.delete('error');
          const newUrl = window.location.pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');
          window.history.replaceState({}, '', newUrl);
          setShowMandatoryModal(false);
        }
      } else {
        showError(res.data?.message || 'Failed to upload image');
      }
    } catch (error) {
      showError('Failed to upload image');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gold">My Profile</h1>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="bg-gold text-dark px-4 py-2 rounded-lg font-medium"
        >
          {isEditing ? 'Cancel' : 'Edit'}
        </button>
      </div>

      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex items-center mb-6">
          <div className="w-24 h-24 relative mr-4">
            <div className="w-24 h-24 bg-gold rounded-full flex items-center justify-center overflow-hidden border-2 border-gold">
              {user?.profile_image ? (
                <img
                  src={`${(process.env.REACT_APP_API_URL || 'https://drvivara.cloud/api').replace('/api', '')}/uploads/profiles/${user.profile_image}`}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-3xl font-bold text-dark">
                  {user?.name?.charAt(0).toUpperCase() || 'D'}
                </span>
              )}
            </div>
            {/* Always-visible camera button - works on iOS touch */}
            <label className="absolute -bottom-1 -right-1 bg-gold rounded-full p-2 cursor-pointer shadow-lg border-2 border-gray-800">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
            </label>
          </div>
          <div>
            <h2 className="text-xl font-semibold">{user?.name || 'Provider'}</h2>
            <p className="text-gray-400 capitalize">{user?.role || 'provider'}</p>
            {user?.is_approved && (
              <span className="bg-green-900 text-green-300 px-2 py-1 rounded-full text-xs mt-1 inline-block">
                Verified
              </span>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-gray-400 mb-2">Full Name</label>
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={profileData.name}
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
              />
            ) : (
              <p className="text-light">{profileData.name}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-400 mb-2">Email</label>
            <p className="text-light">{profileData.email}</p>
          </div>

          <div>
            <label className="block text-gray-400 mb-2">Phone</label>
            {isEditing ? (
              <input
                type="tel"
                name="phone"
                value={profileData.phone}
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
              />
            ) : (
              <p className="text-light">{profileData.phone || 'Not provided'}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-400 mb-2">Specialization</label>
            {isEditing ? (
              <input
                type="text"
                name="specialization"
                value={profileData.specialization}
                onChange={handleInputChange}
                placeholder="e.g., Cardiologist, General Physician"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
              />
            ) : (
              <p className="text-light">{profileData.specialization || 'Not provided'}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-400 mb-2">License Number</label>
            {isEditing ? (
              <input
                type="text"
                name="license_number"
                value={profileData.license_number}
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
              />
            ) : (
              <p className="text-light">{profileData.license_number || 'Not provided'}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-400 mb-2">Address</label>
            {isEditing ? (
              <textarea
                name="address"
                value={profileData.address}
                onChange={handleInputChange}
                rows="3"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
              />
            ) : (
              <p className="text-light">{profileData.address || 'Not provided'}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-400 mb-2">Bio</label>
            {isEditing ? (
              <textarea
                name="bio"
                value={profileData.bio}
                onChange={handleInputChange}
                rows="4"
                placeholder="Tell users about your experience and expertise..."
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
              />
            ) : (
              <p className="text-light">{profileData.bio || 'No bio provided'}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-400 mb-2">Digital Signature (Base64 or URL)</label>
            {isEditing ? (
              <textarea
                name="signature"
                value={profileData.signature}
                onChange={handleInputChange}
                rows="2"
                placeholder="Paste signature image data or URL..."
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
              />
            ) : (
              <div className="mt-2 p-2 bg-white rounded-lg inline-block">
                {profileData.signature ? (
                  <img src={profileData.signature} alt="Signature" className="h-12 object-contain" />
                ) : (
                  <p className="text-gray-500 text-xs italic">No signature uploaded</p>
                )}
              </div>
            )}
          </div>
        </div>

        {isEditing && (
          <button
            onClick={updateProfile}
            className="w-full bg-gold text-dark py-3 rounded-lg font-medium mt-6"
          >
            Save Changes
          </button>
        )}
      </div>

      <div className="bg-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Support & Legal</h3>
        <div className="space-y-3">
          <button
            onClick={() => navigate('/terms-and-conditions')}
            className="w-full text-left py-3 px-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
          >
            Terms & Conditions
          </button>
          <button
            onClick={() => navigate('/privacy-policy')}
            className="w-full text-left py-3 px-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
          >
            Privacy Policy
          </button>
          <button
            onClick={() => navigate('/about-us')}
            className="w-full text-left py-3 px-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
          >
            About Us
          </button>
          <button
            onClick={() => navigate('/patient/support')}
            className="w-full text-left py-3 px-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors flex justify-between items-center"
          >
            <span>Contact Support</span>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </button>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Account Settings</h3>
        <div className="space-y-3">
          <button
            onClick={() => setShowSettingsModal(true)}
            className="w-full text-left py-3 px-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors flex justify-between items-center"
          >
            <span>Change Password / Delete Account</span>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            onClick={handleLogout}
            className="w-full text-left py-3 px-4 bg-red-900 text-red-300 rounded-lg hover:bg-red-800 transition-colors"
          >
            Logout
          </button>
        </div>
      </div>
      {/* Mandatory Profile Image Modal */}
      {showMandatoryModal && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-xl p-8 max-w-md w-full text-center border border-yellow-400 shadow-[0_0_50px_rgba(234,179,8,0.5)]">
            <div className="w-20 h-20 bg-red-900/50 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            <h2 className="text-2xl font-bold text-white mb-2">Profile Photo Required</h2>
            <p className="text-gray-300 mb-6">
              For user safety and trust, all providers <b>must</b> upload a clear profile photo before accessing the platform.
            </p>

            <label className="block w-full bg-gold hover:bg-yellow-500 text-dark font-bold py-3 px-4 rounded-lg cursor-pointer transition-colors">
              <span>Upload Photo Now</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
            </label>

            <p className="text-xs text-gray-500 mt-4">
              Once uploaded, this message will disappear automatically.
            </p>
          </div>
        </div>
      )}

      <AccountSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

    </div>
  );
};

export default DoctorProfile;
